import { useCallback, useEffect, useRef, useState } from 'react';
import api, { apiError } from '@/lib/api';
import { newRequestId } from '@/components/casino/dice/diceModel';
import { cleanBalances, confirmedRound, pendingKey, validPending, validConfig } from './slotsModel';

// Only unresolved identity/settings are persisted. No local outcomes or ledger.
export default function useSlotsGame(userId, refreshWallet) {
  const [round, setRound] = useState(null), [history, setHistory] = useState([]), [config, setConfig] = useState(null);
  const [loading, setLoading] = useState(true), [busy, setBusy] = useState(false), [error, setError] = useState(''), [syncError, setSyncError] = useState('');
  const [historyError, setHistoryError] = useState(false);
  const generation = useRef(0), activeId = useRef(null), pending = useRef(null), current = useRef(null), lock = useRef(false), corrupt = useRef(false), balanceSignature = useRef('');
  const identity = useRef(userId); identity.current = userId;
  const refreshRef = useRef(refreshWallet); refreshRef.current = refreshWallet;
  const key = pendingKey(userId), live = useCallback(gen => generation.current === gen && identity.current === userId, [userId]);
  const wallet = useCallback(value => {
    const balances = cleanBalances(value), signature = JSON.stringify(balances);
    if (signature === balanceSignature.current) return;
    balanceSignature.current = signature;
    try { Promise.resolve(refreshRef.current?.(balances)).catch(() => {}); } catch { /* never replay a wager because auth refresh failed */ }
  }, []);
  const clearPending = useCallback(id => {
    try { if (JSON.parse(sessionStorage.getItem(key))?.request_id === id) sessionStorage.removeItem(key); } catch { /* replay remains safe */ }
    if (pending.current?.request_id === id) pending.current = null;
  }, [key]);
  const accept = useCallback((data, gen) => {
    if (!live(gen) || activeId.current !== data?.request_id) return null;
    const result = confirmedRound(data);
    if (pending.current && ["bet", "currency"].some(field => pending.current[field] !== result[field])) throw new Error("Spin settings did not match your saved request");
    clearPending(result.request_id); corrupt.current = false; current.current = result;
    setRound(result); setError(''); setSyncError('');
    setHistory(rows => [result, ...rows.filter(row => row.request_id !== result.request_id)].slice(0, 30));
    if (result.balances) wallet(result.balances);
    return result;
  }, [clearPending, live, wallet]);
  const loadHistory = useCallback(async () => {
    const gen = generation.current;
    try {
      const { data } = await api.get('/casino/slots/history');
      if (!Array.isArray(data)) throw new Error();
      const rows = data.map(confirmedRound);
      if (live(gen)) {
        const latest = current.current;
        setHistory(latest ? [latest, ...rows.filter(row => row.request_id !== latest.request_id)].slice(0, 30) : rows); setHistoryError(false);
      }
      return rows;
    } catch { if (live(gen)) setHistoryError(true); return []; }
  }, [live]);
  const reconcile = useCallback(async (request, gen) => {
    const { data } = await api.get(`/casino/slots/round/${encodeURIComponent(request.request_id)}`);
    if (!live(gen) || activeId.current !== request.request_id) return null;
    if (data.request_id !== request.request_id) throw new Error('Wrong spin response');
    if (current.current?.request_id === request.request_id) return current.current;
    if (data.status === 'completed') return accept(data, gen);
    if (data.status === 'not_placed' && data.reels == null && data.payout == null) {
      clearPending(request.request_id); setSyncError(''); setError('This spin was not placed. Your wallet was not charged for it.'); return null;
    }
    throw new Error('Your spin is still awaiting confirmation');
  }, [accept, clearPending, live]);
  const connect = useCallback(async () => {
    if (lock.current) return;
    const gen = generation.current; lock.current = true; setLoading(true); setError('');
    try {
      const { data } = await api.get('/casino/slots/config');
      if (!validConfig(data)) throw new Error();
      if (live(gen)) setConfig(data);
    } catch { if (live(gen)) { setConfig(null); setError('Couldn’t load Slots rules. Reconnect before playing.'); } }
    if (!live(gen)) return;
    try {
      if (pending.current) await reconcile(pending.current, gen);
      else {
        const before = activeId.current;
        const { data } = await api.get('/casino/slots/active');
        if (live(gen) && activeId.current === before && !pending.current) {
          if (data.round) { activeId.current = data.round.request_id; accept(data.round, gen); }
          else if (!corrupt.current) { setSyncError(''); if (data.balances) wallet(data.balances); }
        }
      }
    } catch { if (live(gen)) setSyncError('Your last spin is unconfirmed. Resume the same spin before placing another.'); }
    await loadHistory();
    if (live(gen)) { lock.current = false; setLoading(false); }
  }, [accept, live, loadHistory, reconcile, wallet]);
  useEffect(() => {
    generation.current += 1; pending.current = null; current.current = null; activeId.current = null; lock.current = false; corrupt.current = false; balanceSignature.current = '';
    setRound(null); setHistory([]); setConfig(null); setBusy(false); setSyncError(''); setHistoryError(false);
    try {
      const saved = JSON.parse(sessionStorage.getItem(key));
      if (saved && !validPending(saved)) throw new Error();
      pending.current = saved; activeId.current = saved?.request_id || null;
    } catch { corrupt.current = true; setSyncError('Your last spin needs recovery. Reconnect, or contact support if it remains unconfirmed.'); }
    connect(); return () => { generation.current += 1; };
  }, [connect, key]);
  const send = useCallback(async request => {
    if (lock.current) return null;
    const gen = generation.current; lock.current = true; activeId.current = request.request_id; setBusy(true); setError('');
    try {
      const { data } = await api.post('/casino/slots/spin', { request_id: request.request_id, bet: request.bet, currency: request.currency }, { timeout: 15000 });
      return accept(data, gen);
    } catch (err) {
      if (!live(gen)) return null;
      if (err?.response?.status === 422) { clearPending(request.request_id); setError('The spin was not placed. Check your wager settings.'); return null; }
      if (live(gen)) setError(err?.response ? apiError(err.response.data?.detail) : 'Connection interrupted. Checking the same spin…');
      try { return await reconcile(request, gen); }
      catch { if (live(gen)) setSyncError('Your spin is unconfirmed. Resume the same spin before placing another.'); return null; }
    } finally { if (live(gen)) { lock.current = false; setBusy(false); } }
  }, [accept, clearPending, live, reconcile]);
  const play = useCallback(async settings => {
    if (lock.current || loading || !config || config.enabled === false || syncError || pending.current) return null;
    let request;
    try {
      request = { request_id: newRequestId(), bet: settings.bet, currency: settings.currency };
      if (!validPending(request)) throw new Error();
      sessionStorage.setItem(key, JSON.stringify(request));
    } catch { setError('Couldn’t save your spin reference. Check browser storage before playing.'); return null; }
    pending.current = request; current.current = null; setRound(null);
    return send(request);
  }, [config, key, loading, send, syncError]);
  const resume = useCallback(async () => {
    if (lock.current || loading) return null;
    if (pending.current) return send(pending.current);
    await connect(); return null;
  }, [connect, loading, send]);
  return { round, history, config, loading, busy, error, syncError, historyError, pending: pending.current, play, resume, reconnect: connect, loadHistory };
}
